import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { Check, Minus, Filter, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../utils/cn';

export default function MatrixView({ research }) {
  const [filter, setFilter] = useState('');
  const [showCapabilities, setShowCapabilities] = useState(true);
  const [showFeatures, setShowFeatures] = useState(true);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const scrollContainerRef = useRef(null);
  const wrapperRef = useRef(null);
  const [buttonPosition, setButtonPosition] = useState({ top: 0, visible: false });

  // Check scroll state
  const checkScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const hasHorizontalScroll = container.scrollWidth > container.clientWidth;
    const scrollLeft = container.scrollLeft;
    const maxScroll = container.scrollWidth - container.clientWidth;

    setCanScrollLeft(hasHorizontalScroll && scrollLeft > 5);
    setCanScrollRight(hasHorizontalScroll && scrollLeft < maxScroll - 5);
  }, []);

  // Calculate button vertical position based on visible area
  const updateButtonPosition = useCallback(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const rect = wrapper.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    // Calculate the visible portion of the wrapper
    const visibleTop = Math.max(0, rect.top);
    const visibleBottom = Math.min(viewportHeight, rect.bottom);
    const visibleHeight = visibleBottom - visibleTop;

    if (visibleHeight > 50) {
      // Center vertically in the visible portion
      const centerY = visibleTop + visibleHeight / 2;
      // Convert to position relative to wrapper
      const relativeTop = centerY - rect.top;
      setButtonPosition({ top: relativeTop, visible: true });
    } else {
      setButtonPosition({ top: 0, visible: false });
    }
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    checkScroll();
    updateButtonPosition();

    container.addEventListener('scroll', checkScroll);
    window.addEventListener('resize', checkScroll);
    window.addEventListener('scroll', updateButtonPosition, true);
    window.addEventListener('resize', updateButtonPosition);

    return () => {
      container.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
      window.removeEventListener('scroll', updateButtonPosition, true);
      window.removeEventListener('resize', updateButtonPosition);
    };
  }, [research, checkScroll, updateButtonPosition]);

  const scrollLeft = () => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollBy({ left: -200, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };

  // Flatten products with company info (these become columns)
  const products = useMemo(() => {
    return research.companies.flatMap(company =>
      company.products.map(product => ({
        ...product,
        companyName: company.name,
        companyId: company.id,
      }))
    );
  }, [research.companies]);

  // Filter rows (capabilities and features)
  const rows = useMemo(() => {
    const items = [];

    if (showCapabilities) {
      research.capabilities.forEach(cap => {
        if (!filter || cap.name.toLowerCase().includes(filter.toLowerCase())) {
          items.push({ id: cap.id, name: cap.name, type: 'capability' });
        }
      });
    }

    if (showFeatures) {
      research.features.forEach(feat => {
        if (!filter || feat.name.toLowerCase().includes(filter.toLowerCase())) {
          items.push({ id: feat.id, name: feat.name, type: 'feature' });
        }
      });
    }

    return items;
  }, [research.capabilities, research.features, filter, showCapabilities, showFeatures]);

  const hasAttribute = (product, row) => {
    if (row.type === 'capability') {
      return product.capabilityIds.includes(row.id);
    }
    return product.featureIds.includes(row.id);
  };

  return (
    <div className="bg-card rounded-xl shadow-card overflow-hidden relative">
      {/* Filters */}
      <div className="p-4 border-b border-border flex flex-wrap items-center gap-4">
        <div className="relative flex-1 min-w-[200px]">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter capabilities and features..."
            className="w-full pl-10 pr-4 py-2 rounded-lg border border-border bg-background focus:border-secondary focus:ring-1 focus:ring-secondary"
          />
          {filter && (
            <button
              onClick={() => setFilter('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={showCapabilities}
              onChange={(e) => setShowCapabilities(e.target.checked)}
              className="rounded border-border text-secondary focus:ring-secondary"
            />
            Capabilities ({research.capabilities.length})
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={showFeatures}
              onChange={(e) => setShowFeatures(e.target.checked)}
              className="rounded border-border text-secondary focus:ring-secondary"
            />
            Features ({research.features.length})
          </label>
        </div>
      </div>

      {/* Matrix Table - Transposed: rows are features/capabilities, columns are products */}
      <div ref={wrapperRef} className="relative">
        <div ref={scrollContainerRef} className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-muted">
                <th className="sticky left-0 z-20 bg-muted px-4 py-3 text-left text-sm font-medium text-primary border-r border-border min-w-[200px]">
                  Capability / Feature
                </th>
              {products.map((product) => (
                <th
                  key={product.id}
                  className="px-2 py-3 text-center text-xs font-medium text-primary min-w-[100px] max-w-[120px]"
                >
                  <div className="break-words">{product.companyName}</div>
                  <div className="text-muted-foreground font-normal break-words">{product.name}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr
                key={row.id}
                className={cn(
                  "border-b border-border hover:bg-muted/30 transition-colors",
                  idx % 2 === 0 ? "bg-background" : "bg-muted/10"
                )}
              >
                <td className={cn(
                  "sticky left-0 z-10 px-4 py-3 border-r border-border text-sm font-medium bg-card",
                  row.type === 'capability' ? "text-secondary" : "text-accent"
                )}>
                  {row.name}
                </td>
                {products.map((product) => (
                  <td key={product.id} className="px-2 py-3 text-center">
                    {hasAttribute(product, row) ? (
                      <Check className="w-5 h-5 mx-auto text-success" />
                    ) : (
                      <Minus className="w-5 h-5 mx-auto text-muted-foreground/30" />
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          </table>
        </div>

        {/* Scroll buttons */}
        {buttonPosition.visible && canScrollLeft && (
          <button
            onClick={scrollLeft}
            className="absolute left-52 z-30 w-10 h-10 flex items-center justify-center rounded-full bg-white/50 backdrop-blur-sm shadow-lg border border-border hover:bg-white/80 transition-colors"
            style={{ top: buttonPosition.top, transform: 'translateY(-50%)' }}
            aria-label="Scroll left"
          >
            <ChevronLeft className="w-6 h-6 text-secondary" />
          </button>
        )}
        {buttonPosition.visible && canScrollRight && (
          <button
            onClick={scrollRight}
            className="absolute right-2 z-30 w-10 h-10 flex items-center justify-center rounded-full bg-white/50 backdrop-blur-sm shadow-lg border border-border hover:bg-white/80 transition-colors"
            style={{ top: buttonPosition.top, transform: 'translateY(-50%)' }}
            aria-label="Scroll right"
          >
            <ChevronRight className="w-6 h-6 text-secondary" />
          </button>
        )}
      </div>

      {/* Summary */}
      <div className="p-4 border-t border-border text-sm text-muted-foreground">
        Showing {rows.length} attributes × {products.length} products
      </div>
    </div>
  );
}
